import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GlobalContext } from '../../context/Provider';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import sidebarLogo from '../../assets/images/logo/logo2.png';
import sidebarIcon from '../../assets/images/logo/logo-icon.png';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Star,
  DollarSign,
  LogOut,
  X
} from 'lucide-react';

interface TheSideBarProps {
  handled: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

const TheSideBar: React.FC<TheSideBarProps> = ({ handled, onClose }) => {
  const { logOutNow, authState, language_data } = useContext(GlobalContext);
  const location = useLocation();

  const handleLogout = () => {
    Swal.fire({
      title: `${language_data?.[authState.lang]?.common_are_you_sure || 'Are you sure?'}`,
      text: `${language_data?.[authState.lang]?.common_are_sure_want_to || 'Are you sure you want to'} ${language_data?.[authState.lang]?.common_logout || 'log out'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1AA7DE',
      cancelButtonColor: '#ef4444',
      cancelButtonText: `${language_data?.[authState.lang]?.common_cancel || 'Cancel'}`,
      confirmButtonText: `${language_data?.[authState.lang]?.common_yes || 'Yes, Logout'}`,
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        logOutNow();
      }
    });
  };

  const navItems = [
    {
      name: language_data?.[authState.lang]?.common_dashboard || 'Dashboard',
      path: '/?product=DAILY&sales=DAILY',
      pathname: '/',
      icon: LayoutDashboard,
      badge: null,
    },
    {
      name: language_data?.[authState.lang]?.common_product || 'Products',
      path: '/products/1',
      pathname: '/product',
      icon: Package,
      badge: null,
    },
    {
      name: language_data?.[authState.lang]?.common_order || 'Orders',
      path: '/orders/1',
      pathname: '/orders',
      icon: ShoppingCart,
      badge: null,
    },
    {
      name: language_data?.[authState.lang]?.common_rating_reviews || 'Ratings & Reviews',
      path: '/ratings/1',
      pathname: '/ratings',
      icon: Star,
      badge: null,
    },
    {
      name: language_data?.[authState.lang]?.common_earning || 'Earnings',
      path: '/earnings/1',
      pathname: '/earnings',
      icon: DollarSign,
      badge: null,
    },
  ];

  return (
    <div className="h-full w-full bg-white flex flex-col justify-between p-2 select-none">
      {/* Top Section */}
      <div className="space-y-4">
        {/* Brand Header */}
        <div className={`flex items-center ${handled ? 'justify-center px-3' : 'justify-center px-0'} py-2 mb-2`}>
          <Link to="/" className="flex items-center gap-2 overflow-hidden" title="Ready Ecommerce">
            {handled ? (
              <img
                src={sidebarLogo}
                alt="Ready Ecommerce"
                className="h-15 max-w-[190px] w-auto object-contain transition-all"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-1.5 hover:bg-slate-100 transition-colors">
                <img
                  src={sidebarIcon}
                  alt="Ready Ecommerce"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </Link>

        </div>
        {/* Navigation Section */}
        <nav className="space-y-2 pt-1">
          {navItems.map((item, idx) => {
            const isActive =
              item.pathname === '/'
                ? location.pathname === '/' || location.pathname.startsWith('/?')
                : location.pathname.startsWith(item.pathname);

            const Icon = item.icon;

            return (
              <Link
                key={idx}
                to={
                  authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject'
                    ? '#'
                    : item.path
                }
                onClick={(e) => {
                  if (
                    authState?.is_admin_verified === 'pending' &&
                    item.pathname !== '/'
                  ) {
                    e.preventDefault();
                    toast.error('Your account is pending for admin verification.');
                    return;
                  }
                  if (
                    authState?.is_admin_verified === 'reject' &&
                    item.pathname !== '/'
                  ) {
                    e.preventDefault();
                    toast.error('Your account is rejected for admin verification.');
                    return;
                  }
                  if (onClose) {
                    onClose();
                  }
                }}
                className={`flex items-center ${handled ? 'justify-between px-4' : 'justify-center px-0'
                  } py-3.5 rounded-xl text-base font-semibold transition-all group ${isActive
                    ? 'bg-[#1AA7DE] text-white shadow-sm shadow-[#1AA7DE]/20 font-bold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                title={item.name}
              >
                <div className={`flex items-center ${handled ? 'gap-3.5' : 'justify-center'}`}>
                  <Icon
                    className={`w-5.5 h-5.5 transition-colors ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-700'
                      }`}
                  />
                  {handled && <span className="tracking-tight">{item.name}</span>}
                </div>

                {handled && item.badge && (
                  <span
                    className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${isActive ? 'bg-white text-[#1AA7DE]' : 'bg-red-500 text-white shadow-sm'
                      }`}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section: Log Out */}
      <div className="pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={handleLogout}
          className={`w-full flex items-center ${handled ? 'gap-3.5 px-4' : 'justify-center px-0'
            } py-3.5 rounded-xl text-base font-bold text-red-500 hover:bg-red-50/80 transition-all group`}
          title={language_data?.[authState.lang]?.common_logout || 'Log Out'}
        >
          <LogOut className="w-5.5 h-5.5 text-red-500 group-hover:scale-105 transition-transform" />
          {handled && <span>{language_data?.[authState.lang]?.common_logout || 'Log Out'}</span>}
        </button>
      </div>
    </div>
  );
};

export default TheSideBar;